import React, { useContext, useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { useFirestore } from '../helpers/firebaseContext';
import { Tooltip, Badge, NumberFormatter } from '@mantine/core';
import { ConfigContext, GlobalDataContext, MyUserDataContext } from '../contexts';
import { CustomClaims, Customer, CustomerFundData, KfhTradeAccount, Portfolio } from '../types';
import { canViewKfhTradeAccounts, convertKfhTradeAccountsToPortfolio, hasKFHTradeBalance } from '../helpers/customerDetailsHelpers';

interface PositionBadgesProps {
    portfolioData: Portfolio[];
    fundData: CustomerFundData[];
    kfhTradeAccountsData?: KfhTradeAccount[];
}

// Same totals the Positions tab badges show (KWD, rounded). -1 = nothing to show.
export const usePositionTotals = (portfolioData: Portfolio[], fundData: CustomerFundData[], kfhTradeAccountsData?: KfhTradeAccount[]) => {
    const configContext = useContext(ConfigContext);
    const globalDataContext = useContext(GlobalDataContext);
    const userContext = useContext(MyUserDataContext);
    const userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims | undefined;

    // Calculate portfoliosTotalValue
    let portfoliosTotalValue = -1;
    if (portfolioData && portfolioData.length > 0) {
        portfoliosTotalValue = 0;
        portfolioData.forEach(portfolio => {
            portfoliosTotalValue += portfolio.position?.total_capital_market_value ?? 0;
        });
        portfoliosTotalValue = Math.round(portfoliosTotalValue);
    }

    // Calculate fundsTotalValue
    let fundsTotalValue = -1;
    if (fundData && fundData.length > 0) {
        fundsTotalValue = 0;
        fundData.forEach((holding) => {
            let fund = globalDataContext.funds?.find(obj => obj.code === holding.gl_code);
            const isOpenEnded = fund?.type == 'open_ended';
            const currency = holding.currency ?? 'KWD';
            let total = isOpenEnded ? (holding.eunits ?? 0) * (fund?.nav_per_unit ?? 0) : (holding.total_subscriptions ?? 0);
            if (currency === "KWD") {
                fundsTotalValue += total;
            } else {
                let rate = parseFloat(configContext.exchange_rates?.rates[currency] ?? 0);
                fundsTotalValue += (total * rate);
            }
        });
        fundsTotalValue = Math.round(fundsTotalValue);
    }

    // Calculate KFH Trade total (admin / CR only)
    let kfhTradeTotalValue = -1;
    if (canViewKfhTradeAccounts(userClaims) && kfhTradeAccountsData && hasKFHTradeBalance(kfhTradeAccountsData)) {
        const summary = convertKfhTradeAccountsToPortfolio(
            kfhTradeAccountsData,
            configContext.exchange_rates?.rates || {},
        );
        kfhTradeTotalValue = Math.round(summary.position?.total_capital_market_value ?? 0);
    }

    return { portfoliosTotalValue, fundsTotalValue, kfhTradeTotalValue };
};

// Total of the Positions tab badges for one customer, for the KYC wealth check.
// Full-position viewers only (admin / CR, never KFH RMs; employee customers need HR, like the Positions tab); undefined for everyone else.
export const useCustomerPositionsTotal = (customer?: Customer) => {
    const customerId = customer?.id;
    const db = useFirestore();
    const userContext = useContext(MyUserDataContext);
    const userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims | undefined;
    const allowed = !!customerId && canViewKfhTradeAccounts(userClaims) && !userClaims?.kfh_rm && (!customer?.employee || !!userClaims?.hr);
    const [data, setData] = useState<{ portfolios: Portfolio[], funds: CustomerFundData[], kfhTrade: KfhTradeAccount[] }>();

    useEffect(() => {
        if (!allowed) return;
        let cancelled = false;
        const read = (name: string) => getDocs(collection(db, "customers", customerId!, name)).then(s => s.docs.map(d => ({ ...d.data(), id: d.id })) as any[]);
        Promise.all([read("portfolios"), read("fund_data"), read("kfhtrade_accounts")])
            .then(([portfolios, funds, kfhTrade]) => { if (!cancelled) setData({ portfolios, funds, kfhTrade }); })
            .catch(console.error);
        return () => { cancelled = true; };
    }, [db, customerId, allowed]);

    const totals = usePositionTotals(data?.portfolios ?? [], data?.funds ?? [], data?.kfhTrade ?? []);
    if (!allowed || !data) return undefined;
    return Object.values(totals).reduce((sum, v) => sum + Math.max(0, v), 0);
};

const PositionBadges: React.FC<PositionBadgesProps> = ({ portfolioData, fundData, kfhTradeAccountsData }) => {

    const { portfoliosTotalValue, fundsTotalValue, kfhTradeTotalValue } = usePositionTotals(portfolioData, fundData, kfhTradeAccountsData);

    return (
        <>
            {fundsTotalValue >= 0 && (
                <Tooltip label="Funds">
                    <Badge color="pink" size="xs" rightSection="KWD" mr={5}>
                        <NumberFormatter value={fundsTotalValue} thousandSeparator="," />
                    </Badge>
                </Tooltip>
            )}
            {portfoliosTotalValue >= 0 && (
                <Tooltip label="Portfolios">
                    <Badge color="purple" size="xs" rightSection="KWD" mr={5}>
                        <NumberFormatter value={portfoliosTotalValue} thousandSeparator="," />
                    </Badge>
                </Tooltip>
            )}
            {kfhTradeTotalValue >= 0 && (
                <Tooltip label="KFH Trade">
                    <Badge color="orange" size="xs" rightSection="KWD" mr={5}>
                        <NumberFormatter value={kfhTradeTotalValue} thousandSeparator="," />
                    </Badge>
                </Tooltip>
            )}
        </>
    );
};

export default PositionBadges;
